import { Box, Grid, Typography } from "@mui/material"
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"
import { round_with_spaces } from "../../../utils/functions"
import theme_colors from "../../../configs/theme_colors.json"

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0]

    const total = payload[0].payload.total // we’ll add this below
    const percent = ((value / total) * 100).toFixed(1)

    const displayName =
      name_lookup[name] || name.charAt(0).toUpperCase() + name.slice(1)

    return (
      <div
        style={{
          background: "white",
          border: "1px solid #ccc",
          padding: "8px",
          borderRadius: "4px",
        }}
      >
        <strong>{displayName}</strong>: {round_with_spaces(Math.round(value))}{" "}
        people
        <br />
        <strong>Percentage: </strong>
        {percent}%
      </div>
    )
  }

  return null
}

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  name,
}) => {
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
    >
      {name_lookup[name]}
    </text>
  )
}

const name_lookup = {
  male: "Male",
  female: "Female",
  "0_14": "0 to 14",
  "15_24": "15 to 24",
  "25_44": "25 to 44",
  "45_64": "45 to 64",
  "65_plus": "65 plus",
}

export default function Demographics({ stats }) {
  const colours = [
    theme_colors.bar_1,
    theme_colors.bar_2,
    theme_colors.bar_3,
    theme_colors.bar_4,
    theme_colors.bar_5,
  ]

  const stats_sex = Object.entries(stats.sex).map(([key, value]) => ({
    name: key,
    value,
  }))
  const stats_age = Object.entries(stats.age).map(([key, value]) => ({
    name: key,
    value,
  }))
  const total_sex = stats_sex.reduce((sum, d) => sum + d.value, 0)
  const total_age = stats_age.reduce((sum, d) => sum + d.value, 0)
  const enriched_data_sex = stats_sex.map((d) => ({ ...d, total: total_sex }))
  const enriched_data_age = stats_age.map((d) => ({ ...d, total: total_age }))

  return (
    <Box sx={{ flexGrow: 1, padding: 2 }}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={6}>
          <Typography sx={{ width: "100%" }} align="center" variant="h5">
            Sex
          </Typography>
          <Box sx={{ width: "100%", height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  label={renderCustomizedLabel}
                  labelLine={false}
                  data={enriched_data_sex}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="100%"
                  startAngle={90}
                  endAngle={-360}
                >
                  {enriched_data_sex.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colours[index % colours.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography sx={{ width: "100%" }} align="center" variant="h5">
            Age
          </Typography>
          <Box sx={{ width: "100%", height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  label={renderCustomizedLabel}
                  labelLine={false}
                  data={enriched_data_age}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="100%"
                  startAngle={90}
                  endAngle={-360}
                >
                  {enriched_data_age.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colours[index % colours.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
